// React
import React, { useMemo, useCallback, useState } from 'react'

// Next
import Link from 'next/link'
import Image from 'next/image'

// Types
import { MemeTemplate } from '@supermeme-ai/types'

// Components
import { Button } from '@/components/ui/button'

// Utils
import { shimmer } from '@/utils/ui/shimmer-placeholder'
import { toBase64 } from '@/utils/index'

// External
import { Loader2, PenSquare, Sparkles } from 'lucide-react'
import { useTranslation } from 'next-i18next'

interface RawMemeTemplateProps extends MemeTemplate {
  uploadedTemplate?: boolean
  showEditButton?: boolean
  onSelect?: (template: MemeTemplate) => void
}

const GenerateButton: React.FC<{
  loading: boolean
  onClick: () => void
  disabled?: boolean
}> = React.memo(({ loading, onClick, disabled }) => {
  const { t } = useTranslation('components/raw-meme-template')
  return (
    <Button
      size='sm'
      variant='outline'
      className='w-full'
      type='button'
      disabled={disabled || loading}
      onClick={onClick}
    >
      {loading ? (
        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
      ) : (
        <Sparkles className='mr-2 h-4 w-4' />
      )}
      <span>{t('buttons.generateWithAi.label')}</span>
    </Button>
  )
})

GenerateButton.displayName = 'GenerateButton'

const EditButton: React.FC<{ href: string }> = React.memo(({ href }) => {
  const { t } = useTranslation('components/raw-meme-template')
  const [loading, setLoading] = useState(false)
  return (
    <Button
      asChild
      size='sm'
      variant='outline'
      className='w-full'
      disabled={loading}
      onClick={() => setLoading(true)}
    >
      <Link href={href}>
        {loading ? (
          <Loader2 className='mr-2 h-4 w-4 animate-spin' />
        ) : (
          <PenSquare className='mr-2 h-4 w-4' />
        )}
        <span>{t('buttons.edit.label')}</span>
      </Link>
    </Button>
  )
})

EditButton.displayName = 'EditButton'

const RawMemeTemplate: React.FC<RawMemeTemplateProps> = React.memo(
  ({
    name,
    image_path,
    meme_text,
    description,
    uploadedTemplate,
    showEditButton,
    onSelect,
  }) => {
    const { t } = useTranslation('components/raw-meme-template')
    const parameter = useMemo(
      () =>
        toBase64(
          JSON.stringify({
            meme_text: (meme_text || '').replace("'", "'"),
            image_path: (image_path || '').replace("'", "'"),
            description: (description || '').replace("'", "'"),
          })
        ),
      [meme_text, image_path, description]
    )

    const handleSelect = useCallback(() => {
      onSelect?.({ name, image_path, meme_text, description })
    }, [onSelect, name, image_path, meme_text, description])

    return (
      <div className='m-auto flex h-full w-full flex-col items-center justify-center space-y-2 rounded-lg bg-gray-100 p-2'>
        <div className='flex h-full w-full items-center justify-center'>
          <div id={image_path} className='relative h-[260px] w-[300px]'>
            <Image
              src={image_path}
              alt={meme_text || 'Meme template'}
              fill
              placeholder='blur'
              blurDataURL={`data:image/svg+xml;base64,${toBase64(shimmer(200, 200))}`}
              className='object-scale-down'
            />
          </div>
        </div>
        <div className='flex w-full flex-col space-x-0 space-y-2 xl:flex-row xl:space-x-2 xl:space-y-0'>
          {onSelect ? (
            <GenerateButton loading={false} onClick={handleSelect} />
          ) : (
            <Button size='sm' variant='outline' asChild className='w-full'>
              <Link
                href={`${typeof window !== 'undefined' ? window.location.origin : ''}/meme-generator?template=${parameter}&uploadedTemplate=${uploadedTemplate ?? false}`}
              >
                <Sparkles className='mr-2 h-4 w-4' />
                <span>{t('buttons.generateWithAi.label')}</span>
              </Link>
            </Button>
          )}
          {showEditButton && !name && (
            <EditButton
              href={`${typeof window !== 'undefined' ? window.location.origin : ''}/meme-maker?template=${parameter}&uploadedTemplate=${uploadedTemplate ?? false}`}
            />
          )}
          {showEditButton && !!name && (
            <EditButton href={`/meme/${name.replace(/\.[^/.]+$/, '')}`} />
          )}
        </div>
      </div>
    )
  }
)

RawMemeTemplate.displayName = 'RawMemeTemplate'

export { RawMemeTemplate }
